
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { 
  Calendar, Clock, FileText, CheckCircle2, AlertCircle, ArrowRight, 
  Info, Shield, Timer, BookOpen, Eye
} from 'lucide-react';
import { useLoading } from '../hooks/useLoading';

export const Assessments = () => {
  const [activeTab, setActiveTab] = useState('upcoming');
  // const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();
    const { setIsLoading } = useLoading();
  
  useEffect(() => {
    // Simulate data loading
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800);
    return () => clearTimeout(timer);
  }, []);
  
  // Mock assessment data
  const upcomingAssessments = [
    {
      id: 1,
      title: 'JavaScript Fundamentals',
      course: 'Python Full Stack Developer',
      courseId: '1',
      date: 'June 20, 2023',
      time: '10:00 AM - 12:00 PM',
      duration: '2 hours',
      totalQuestions: 40,
      passingScore: '70%',
      status: 'upcoming'
    },
    {
      id: 2,
      title: 'Python Data Structures',
      course: 'Python Full Stack Developer',
      courseId: '1',
      date: 'June 25, 2023',
      time: '2:00 PM - 4:00 PM',
      duration: '2 hours',
      totalQuestions: 35,
      passingScore: '65%',
      status: 'upcoming'
    }
  ];
  
  const completedAssessments = [
    {
      id: 3,
      title: 'HTML & CSS Basics',
      course: 'Python Full Stack Developer',
      courseId: '1',
      date: 'June 10, 2023',
      score: 85,
      passingScore: 70,
      totalQuestions: 30,
      correctAnswers: 26,
      status: 'passed'
    },
    {
      id: 4,
      title: 'Introduction to Python',
      course: 'Python Full Stack Developer',
      courseId: '1',
      date: 'June 5, 2023',
      score: 92,
      passingScore: 65,
      totalQuestions: 25,
      correctAnswers: 23,
      status: 'passed'
    },
    {
      id: 5,
      title: 'Git Version Control',
      course: 'Python Full Stack Developer',
      courseId: '1',
      date: 'May 30, 2023',
      score: 64,
      passingScore: 70,
      totalQuestions: 20,
      correctAnswers: 13,
      status: 'failed'
    }
  ];

  // Handle start assessment
  const handleStartAssessment = (courseId: string) => {
    // Navigate to take assessment page with the course ID
    navigate(`/assessments/take/${courseId}`);
  };

  // Handle prepare for assessment
  const handlePrepareForAssessment = (assessmentId: number) => {
    toast({
      title: "Preparation Resources",
      description: "Assessment preparation resources are now available in your study materials.",
    });
  };

  // Handle view assessment details
  const handleViewAssessmentDetails = (assessmentId: number) => {
    toast({
      title: "Assessment Details",
      description: "Detailed assessment information will be available soon.",
    });
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Assessments</h1>
        <p className="text-bytesplash-gray-500">Track your progress through quizzes and exams</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="shadow-soft">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
              </div>
              <div className="text-right">
                <p className="text-sm text-bytesplash-gray-500">Passed</p>
                <p className="text-2xl font-bold">{completedAssessments.filter(a => a.status === 'passed').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="shadow-soft">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <div className="text-right">
                <p className="text-sm text-bytesplash-gray-500">Failed</p>
                <p className="text-2xl font-bold">{completedAssessments.filter(a => a.status === 'failed').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="shadow-soft">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="w-12 h-12 rounded-full bg-bytesplash-light flex items-center justify-center">
                <Clock className="h-6 w-6 text-bytesplash-primary" />
              </div>
              <div className="text-right">
                <p className="text-sm text-bytesplash-gray-500">Upcoming</p>
                <p className="text-2xl font-bold">{upcomingAssessments.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs 
        defaultValue="upcoming" 
        className="space-y-6"
        onValueChange={(value) => setActiveTab(value)}
      >
        <TabsList>
          <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
          <TabsTrigger value="completed">Completed</TabsTrigger>
          <TabsTrigger value="practice">Practice Tests</TabsTrigger>
        </TabsList>
        
        <TabsContent value="upcoming">
          {upcomingAssessments.length > 0 ? (
            <div className="space-y-4">
              {upcomingAssessments.map((assessment) => (
                <Card key={assessment.id} className="shadow-soft overflow-hidden">
                  <div className="bg-bytesplash-light px-6 py-4 flex items-center justify-between">
                    <h3 className="font-semibold text-bytesplash-primary">{assessment.title}</h3>
                    <Badge variant="outline" className="text-bytesplash-primary border-bytesplash-primary">
                      {assessment.course}
                    </Badge>
                  </div>
                  <CardContent className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center gap-2">
                        <Calendar size={18} className="text-bytesplash-gray-500" />
                        <span className="text-bytesplash-gray-700">{assessment.date}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock size={18} className="text-bytesplash-gray-500" />
                        <span className="text-bytesplash-gray-700">{assessment.time}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <FileText size={18} className="text-bytesplash-gray-500" />
                        <span className="text-bytesplash-gray-700">{assessment.totalQuestions} Questions</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 size={18} className="text-bytesplash-gray-500" />
                        <span className="text-bytesplash-gray-700">Passing Score: {assessment.passingScore}</span>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 justify-end">
                      <Button 
                        variant="outline"
                        onClick={() => handlePrepareForAssessment(assessment.id)}
                      >
                        Prepare
                      </Button>
                      <Button 
                        className="bg-bytesplash-primary hover:bg-bytesplash-dark"
                        onClick={() => handleStartAssessment(assessment.courseId)}
                      >
                        Start Assessment
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="shadow-soft">
              <CardContent className="p-8 text-center">
                <h3 className="font-medium text-lg mb-2">No upcoming assessments</h3>
                <p className="text-bytesplash-gray-500 mb-4">
                  You don't have any scheduled assessments at this time.
                </p>
                <Button onClick={() => setActiveTab('practice')}>
                  Try Practice Tests
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        <TabsContent value="completed">
          {completedAssessments.length > 0 ? (
            <div className="space-y-4">
              {completedAssessments.map((assessment) => (
                <Card key={assessment.id} className="shadow-soft overflow-hidden">
                  <div className={`px-6 py-4 flex items-center justify-between ${
                    assessment.status === 'passed' ? 'bg-green-50' : 'bg-red-50'
                  }`}>
                    <h3 className="font-semibold">{assessment.title}</h3>
                    <Badge className={`${
                      assessment.status === 'passed' 
                        ? 'bg-green-100 text-green-800 hover:bg-green-100' 
                        : 'bg-red-100 text-red-800 hover:bg-red-100'
                    }`}>
                      {assessment.status === 'passed' ? 'Passed' : 'Failed'}
                    </Badge>
                  </div>
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
                      <div className="flex items-center gap-2 mb-2 md:mb-0">
                        <Badge variant="outline" className="text-bytesplash-primary border-bytesplash-primary">
                          {assessment.course}
                        </Badge>
                        <span className="text-sm text-bytesplash-gray-500">{assessment.date}</span>
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold mb-1">
                          {assessment.score}%
                        </div>
                        <div className="text-sm text-bytesplash-gray-500">
                          {assessment.correctAnswers} of {assessment.totalQuestions} correct
                        </div>
                      </div>
                    </div>
                    <div className="h-2 bg-bytesplash-gray-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full ${
                          assessment.status === 'passed' ? 'bg-green-500' : 'bg-red-500'
                        }`} 
                        style={{ width: `${assessment.score}%` }}
                      ></div>
                    </div>
                    <div className="mt-4 flex justify-between items-center">
                      <div className="text-sm text-bytesplash-gray-500">
                        Passing score: {assessment.passingScore}%
                      </div>
                      <Button 
                        variant="outline"
                        onClick={() => handleViewAssessmentDetails(assessment.id)}
                      >
                        View Details
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="shadow-soft">
              <CardContent className="p-8 text-center">
                <h3 className="font-medium text-lg mb-2">No completed assessments</h3>
                <p className="text-bytesplash-gray-500 mb-4">
                  You haven't completed any assessments yet.
                </p>
                <Button onClick={() => setActiveTab('upcoming')}>
                  View Upcoming Assessments
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        <TabsContent value="practice">
          <div className="space-y-6">
            <Card className="shadow-soft">
              <CardHeader>
                <CardTitle>Practice Tests</CardTitle>
                <CardDescription>
                  Prepare for your upcoming assessments with these practice tests
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    { title: 'HTML Basics', questions: 20, time: '30 minutes', category: 'Web Development', courseId: '1' },
                    { title: 'CSS Fundamentals', questions: 25, time: '45 minutes', category: 'Web Development', courseId: '1' },
                    { title: 'JavaScript Core Concepts', questions: 30, time: '60 minutes', category: 'Programming', courseId: '1' },
                    { title: 'Python Basics', questions: 25, time: '45 minutes', category: 'Programming', courseId: '1' },
                    { title: 'React Components', questions: 20, time: '40 minutes', category: 'Frontend', courseId: '1' },
                    { title: 'Node.js Essentials', questions: 20, time: '40 minutes', category: 'Backend', courseId: '1' },
                  ].map((test, index) => (
                    <Card key={index} className="shadow-sm card-hover h-full">
                      <CardContent className="p-5">
                        <div className="flex justify-between items-start mb-3">
                          <h3 className="font-medium">{test.title}</h3>
                          <Badge variant="outline" className="text-xs">
                            {test.category}
                          </Badge>
                        </div>
                        <div className="space-y-1 text-sm text-bytesplash-gray-500 mb-4">
                          <div className="flex items-center gap-1">
                            <FileText size={14} />
                            <span>{test.questions} questions</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock size={14} />
                            <span>{test.time}</span>
                          </div>
                        </div>
                        <Button 
                          variant="outline" 
                          className="w-full"
                          onClick={() => handleStartAssessment(test.courseId)}
                        >
                          <span>Start Practice</span>
                          <ArrowRight size={16} className="ml-2" />
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            <Card className="shadow-soft">
              <CardHeader>
                <CardTitle>Assessment Rules & Guidelines</CardTitle>
                <CardDescription>
                  Important information for taking assessments on ByteSplash
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-bytesplash-light/40 p-4 rounded-lg mb-4">
                  <h3 className="flex items-center gap-2 font-medium mb-3 text-bytesplash-primary">
                    <Shield size={18} />
                    Assessment Integrity
                  </h3>
                  <div className="space-y-2 text-sm">
                    <p className="flex items-start gap-2">
                      <Info size={14} className="mt-1 text-bytesplash-primary" />
                      <span>
                        Our assessment platform monitors for suspicious activities such as tab switching, 
                        browser minimizing, or copying content. These actions may be flagged as potential 
                        violations of the assessment integrity policy.
                      </span>
                    </p>
                    <p className="flex items-start gap-2">
                      <Info size={14} className="mt-1 text-bytesplash-primary" />
                      <span>
                        After three warnings, the assessment will be automatically submitted and may result in disqualification.
                      </span>
                    </p>
                  </div>
                </div>
                
                <div className="bg-bytesplash-light/40 p-4 rounded-lg">
                  <h3 className="flex items-center gap-2 font-medium mb-3 text-bytesplash-primary">
                    <Timer size={18} />
                    Time Management
                  </h3>
                  <div className="space-y-2 text-sm">
                    <p className="flex items-start gap-2">
                      <Info size={14} className="mt-1 text-bytesplash-primary" />
                      <span>
                        Assessments have strict time limits. The timer starts once you begin the assessment and 
                        cannot be paused.
                      </span>
                    </p>
                    <p className="flex items-start gap-2">
                      <Info size={14} className="mt-1 text-bytesplash-primary" />
                      <span>
                        If you do not submit before the time expires, your assessment will be automatically submitted 
                        with your current answers.
                      </span>
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="shadow-soft">
              <CardHeader>
                <CardTitle>Assessment Resources</CardTitle>
                <CardDescription>
                  Additional materials to help you prepare
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 border border-bytesplash-gray-200 rounded-lg hover:bg-bytesplash-light/30 transition-colors">
                    <h3 className="font-medium mb-2">Study Guides</h3>
                    <p className="text-sm text-bytesplash-gray-600 mb-3">
                      Comprehensive study materials for all assessment topics.
                    </p>
                    <Button variant="link" className="p-0 h-auto text-bytesplash-primary">
                      Browse Guides
                    </Button>
                  </div>
                  
                  <div className="p-4 border border-bytesplash-gray-200 rounded-lg hover:bg-bytesplash-light/30 transition-colors">
                    <h3 className="font-medium mb-2">Flash Cards</h3>
                    <p className="text-sm text-bytesplash-gray-600 mb-3">
                      Quick review cards for key concepts and terminology.
                    </p>
                    <Button variant="link" className="p-0 h-auto text-bytesplash-primary">
                      View Flash Cards
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
